/* Copyright(c) 2026 APSS Media Pvt. Ltd.
*  All Rights Reserved
*/
/**
 * @file Modigie-5879 
 * @author  Chaitanya Shinde
 * @section DESCRIPTION Modigie Integration-5879-UI (Excel Upload + Job Type Selection + Preview)
*/

import React from "react";
import Navigation from "../layouts/MBAdminNavPage";
import Footer from "../layouts/footer";
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { logoutUser } from '../login/authentication';
import { withRouter } from 'react-router-dom';
import { Table, Progress, Radio, Button, Upload, Alert, Tooltip } from 'antd';
import { UploadOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import { css } from "emotion";

const xlsx = require("xlsx");
const Swal = require('sweetalert2');

const lastBatchCardCSS = css`
  background: linear-gradient(to right, #f4f6ff, #ffffff);
  border: 1px solid #d6d9ff;
  border-left: 6px solid #007cc3;
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 16px;

  .label {
    font-weight: 600;
    color: #007cc3;
    margin-right: 4px;
  }

  .value {
    color: #333;
  }

  .status {
    font-weight: 600;
  }
`;

const historyTableCSS = css`
  background-color: white;
  
  .ant-table {
    border-radius: 6px;
  }

  .ant-table-thead > tr > th {
    background-image: linear-gradient(to right, #28166f, #007cc3) !important;
    color: #ffffff !important;
    text-align: center !important;
    font-weight: 500;
    border: 1px solid #e0e0e0;
  }

  .ant-table-tbody > tr > td {
    text-align: center !important;
    border: 1px solid #e0e0e0;
    padding: 6px 8px;
  }
    
`;

const processBtnDisabledCSS = css`
    &&& {
        background-color: #b5b5b5 !important;
        color: #ffffff !important;
        border: none !important;
        cursor: not-allowed !important;
        opacity: 1 !important;  /* fix faded look */
    }
`;

const processBtnCSS = css`
    background-image: linear-gradient(to right,#28166f,#007cc3);
    color: #ffffff !important;
    border: none !important;
`;

const auditBtnCSS = css`
    background-image: linear-gradient(to right,#007cc3,#28166f);
    color: #ffffff !important;
    border: none !important;
`;

const ellipsisText = css`
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`;

const tableEllipsis = css`
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`;

const JOB_REQUIREMENTS = {
  verifyEmployment: {
    label: 'Verify Employment',
    requiredColumns: ['first_name', 'last_name', 'company_name'],
    requiredMessage:
      'Excel must contain First Name, Last Name, Company Name and LinkedIn URL'
  },
  enrichMobile: {
    label: 'Enrich Mobile & Verify Employment',
    requiredColumns: ['first_name', 'last_name', 'company_name'],
    requiredMessage:
      'Excel must contain First Name, Last Name, Company Name and LinkedIn URL'
  }
};

class Modigie extends React.Component {
    constructor() {
        super();
        this.state = {
            file: null,
            jobType: 'enrichMobile',
            previewData: [],
            totalRecords: 0,
            validRecords: 0,
            invalidRecords: 0,
            uploadError: null,
            batchStatus: null,
            batchId: null,
            progress: 0,
            lastFileName: null,
            lastJobType: null,
            lastRecordCount: 0,
            batchHistory: [],
            isProcessingClicked: null,
        }
    }

    safeFetch = async (url, options = {}) => {
        try {
            const res = await fetch(url, options);

            if (!res.ok) {
                throw new Error('Request failed');
            }

            return res;
        } catch (err) {
            console.error('API error:', err);

            Swal.fire({
            title: 'Server temporarily unavailable',
            text: 'Please try again in a few seconds',
            icon: 'error',
            confirmButtonText: 'Ok'
        }).then(() => {
            window.location.reload();
        });

            throw err;
        }
    };

    componentDidMount() {
        this.restoreLastBatch();
        this.loadBatchHistory();
    }

    restoreLastBatch = async () => {
    const res = await this.safeFetch('/modigie/status/latest');
    const data = await res.json();

    // console.log('Latest batch from backend:', data);
    
    if (data && data.batchId) {
        this.setState({
        batchId: data.batchId,
        batchStatus: data.status,
        lastFileName: data.fileName,
        lastJobType: data.jobType,
        lastRecordCount: data.recordCount
        });

        this.startStatusPolling(data.batchId);
    }
    };

    loadBatchHistory = async () => {
    const res = await this.safeFetch('/modigie/history');
    const data = await res.json();
    this.setState({ batchHistory: data });
    };

    /** Handle Excel Upload */
    
    handleFileUpload = (file) => {
        this.setState({ 
            file,
            processingFileName: null   // reset lock when new file uploaded
         });

        const reader = new FileReader();

        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = xlsx.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = xlsx.utils.sheet_to_json(sheet, { defval: '' });

            // Empty Excel file check
            if (!jsonData.length) {
                this.setState({ uploadError: 'Excel file is empty' });
                return;
            }

            const HEADER_ALIASES = {
                firstname: 'first_name',
                first_name: 'first_name',
                "first name": 'first_name',

                lastname: 'last_name',
                last_name: 'last_name',
                "last name": 'last_name',

                company: 'company_name',
                companyname: 'company_name',
                company_name: 'company_name',
                "company name": 'company_name',

                linkedin: 'linkedin_url',
                linkedinlink: 'linkedin_url',
                linkedin_link: 'linkedin_url',
                linkedinurl: 'linkedin_url',
                linkedin_url: 'linkedin_url',
                "linkedin link": 'linkedin_url',

                phone: 'phone'
            };

            // Normalize headers
            // const normalizeKey = (key) =>
            //     key.toString().trim().toLowerCase().replace(/\s+/g, "_");
            const normalizeKey = (key) => {
            const cleaned = key.toString().trim().toLowerCase();
            const compact = cleaned.replace(/\s+/g, '');

            return HEADER_ALIASES[compact] ||
                    HEADER_ALIASES[cleaned] ||
                    cleaned.replace(/\s+/g, "_");
            };

            const normalizedData = jsonData.map(row => {
                const newRow = {};
                Object.keys(row).forEach(k => {
                    newRow[normalizeKey(k)] = row[k];
                });
                return newRow;
            });

            // Required columns
            const requiredColumns = JOB_REQUIREMENTS[this.state.jobType].requiredColumns;
            const excelColumns = Object.keys(normalizedData[0] || {});
            const missing = requiredColumns.filter(col => !excelColumns.includes(col));

            if (missing.length > 0) {
                this.setState({
                    uploadError: `Missing mandatory column(s) for ${ JOB_REQUIREMENTS[this.state.jobType].label } : ${missing.join(', ')}`,
                    previewData: [],
                    fullData: [],
                    totalRecords: 0,
                    validRecords: 0,
                    invalidRecords: 0
                });
                return;
            }

            // Validate rows
            let valid = 0;
            let invalid = 0;

            // validate all rows
            const validatedRows = normalizedData.map(r => {
                const hasIdentity = Boolean(r.linkedin_url);
                // email OR linkedin mandatory for enrichMobile and verify employment
                // LinkedIn URL validation
                let isLinkedInValid = true;

                if (r.linkedin_url) {
                    isLinkedInValid = r.linkedin_url.toLowerCase().includes('linkedin.com');
                }

                const isValid = r.first_name && r.last_name && r.company_name && hasIdentity && isLinkedInValid ;

                if (!isLinkedInValid) {
                    invalid++;
                    return {
                        ...r,
                        isValid: false,
                        errorReason: 'Invalid LinkedIn URL'
                    };
                }
                // increment counters here
                if (isValid) valid++;
                else invalid++;

                return {
                    ...r,
                    isValid,
                    errorReason: !hasIdentity
                    ? 'LinkedIn URL required'
                    : !isLinkedInValid
                    ? 'Invalid LinkedIn URL'
                    : null
                };
            });

            // SINGLE setState with all variables in scope
            this.setState({
                previewData: validatedRows.slice(0, 50) ,
                fullData: validatedRows,   // used for backend upload
                totalRecords: validatedRows.length,
                validRecords: valid,
                invalidRecords: invalid,
                uploadError: null
            });
        };

        reader.readAsArrayBuffer(file);
        return false; // Prevent auto-upload
    };

    /** Start Modigie Processing */

    handleProcess = async () => {
    
    if (this.state.processingFileName === this.state.file.name) {
        return;   // prevent double processing for same file
    }

    this.setState({ processingFileName: this.state.file.name });

    try {
        const reader = new FileReader();
        reader.onload = async () => {
        const base64File = reader.result.split(',')[1];

        const payload = {
            fileName: this.state.file.name,
            fileBase64: base64File,
            jobType: this.state.jobType,
            rows: this.state.fullData
        };

        const res = await this.safeFetch('/modigie/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await res.json();

        if (!data.batchId) {
            Swal.fire('Upload failed', '', 'error');
            return;
        }

        /* STORE batchId + disable process button */
        this.setState({
            batchId: data.batchId,
            batchStatus: 'processing'
        });

        await this.safeFetch(`/modigie/process/${data.batchId}`, {
            method: 'POST'
        });

        Swal.fire('Processing Started', 'Batch submitted to Modigie', 'success');
        this.startStatusPolling(data.batchId);
        };

        reader.readAsDataURL(this.state.file);
    } catch (err) {
        Swal.fire('Error', 'Something went wrong', 'error');
    }
    };

    startStatusPolling = (batchId) => {
    this.poller = setInterval(async () => {
        const res = await this.safeFetch(`/modigie/status/${batchId}`);
        const data = await res.json();

        const total = data.stats.total || 1;
        const done = Number(data.stats.completed) + Number(data.stats.failed);
        const percent = Math.round((done / total) * 100);

        this.setState({
        batchStatus: data.batch.status,
        progress: percent
        });

        // FINALIZE ONCE
        if (data.batch.status === 'completed') {
        clearInterval(this.poller);

        // Refresh UI from DB (single source of truth)
        await this.restoreLastBatch();
        await this.loadBatchHistory();
        }
    }, 5000);
    };

    /** Handle Audit Report */
    handleAuditReport = () => {
        const XLSX = require("xlsx");

        const data = this.state.previewData.length
            ? [...this.state.previewData]
            : [];

        if (data.length === 0) {
            Swal.fire("No data to export", "", "warning");
            return;
        }

        // Create sheet
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Apply red highlight for invalid rows
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        for (let row = 1; row <= range.e.r; row++) {
            const record = data[row - 1];
            if (!record.isValid) {
                for (let col = 0; col <= range.e.c; col++) {
                    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                    if (!worksheet[cellRef]) continue;

                    worksheet[cellRef].s = {
                        fill: {
                            fgColor: { rgb: "FFE6E6" }  // Light red
                        }
                    };
                }
            }
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Report");

        XLSX.writeFile(workbook, "Modigie_Audit_Report.xlsx");

        Swal.fire("Audit Report Generated", "Please open the Excel file for corrections.", "success");
    };

    downloadOutput = async (batchId) => {
    try {
        const response = await this.safeFetch(`/modigie/download/${batchId}`);

        if (!response.ok) {
        throw new Error('File not ready');
        }

        const blob = await response.blob();

        // Extract filename from response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = 'Modigie_Output.xlsx';

        if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match && match[1]) {
            fileName = match[1];
        }
        }

        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (err) {
        console.error(err);
        Swal.fire('Download failed', 'Output file not ready yet', 'error');
    }
    };

    render() {
        const columns = [
            { title: 'First Name', dataIndex: 'first_name', key: 'first_name' },
            { title: 'Last Name', dataIndex: 'last_name', key: 'last_name' },
            { title: 'Company', dataIndex: 'company_name', key: 'company_name' },
            { title: 'Phone', dataIndex: 'phone', key: 'phone' },
            { title: 'LinkedIn', dataIndex: 'linkedin_url', key: 'linkedin_url' }
        ];
        
        const historyColumns = [
        {
            title: 'File Name',
            dataIndex: 'file_name',
            align: 'center',
            width: 250,
            render: (fileName) => (
                <Tooltip title={fileName}>
                    <span className={tableEllipsis}>
                        {fileName}
                    </span>
                </Tooltip>
            )
        },
        {
            title: 'Job Type',
            dataIndex: 'job_type',
            align: 'center',
            width: 180,
            render: jt =>
            jt === 'verifyEmployment'
                ? 'Verify Employment'
                : 'Enrich Mobile & Verify Employment'
        },
        {
            title: 'Records',
            dataIndex: 'record_count',
            align: 'center',
            width: 120
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'center',
            width: 140,
            render: s => (
            <span
                style={{
                fontWeight: 600,
                color:
                    s === 'completed'
                    ? '#52c41a'
                    : s === 'processing'
                    ? '#faad14'
                    : '#ff4d4f'
                }}
            >
                {s.toUpperCase()}
            </span>
            )
        },
        {
            title: 'Action',
            align: 'center',
            width: 150,
            render: (_, row) =>
            row.status === 'completed' ? (
            <Button
                size="small"
                className={processBtnCSS}
                onClick={() => this.downloadOutput(row.id)}
            >
                Download
            </Button>
            ) : (
                <span>-</span>
            )
        }
        ];

        const invalidRowStyle = css`
            .invalid-row > td {
                background-color: #ffe6e6 !important;
            }`;

        // --- NEW: Mandatory red asterisk style ---
        const Required = () => <span style={{ color: "red", marginLeft: 3 }}>*</span>;

        return (
            <div>
                <div>
                    <nav className="navbar navbar-expand-md navbar-dark navbar-fixed-top" >
                        <div style={{ float: 'left' }}>
                            <img src="Leads-Nexus.png" alt="Leads Nexus" height="50px" width="200px"></img>
                        </div>
                    </nav>
                </div>
                <div className="container-fluid" style={{ paddingTop: '100px', paddingBottom: '5%' }}>
                    <div className="row">
                        <div className="col-lg-12" align="center">
                            <label style={{ fontSize: '28px', fontWeight: '500', color: '#193D8F' }}>Modigie Integration</label>
                        </div>
                    </div>

                    <div className="card mt-3">
                        <div className="card-body">

                            {/* Job Type Selection */}
                            <div className="mb-3">
                                <label><b>Select Job Type</b></label><br />
                                <Radio.Group
                                    value={this.state.jobType}
                                    onChange={(e) => 
                                        this.setState({
                                            jobType: e.target.value,
                                            uploadError: null,
                                            previewData: [],
                                            totalRecords: 0,
                                            validRecords: 0,
                                            invalidRecords: 0,
                                            file: null
                                            })
                                        }
                                >
                                    <Radio value="enrichMobile">Enrich Mobile & Verify Employment</Radio>
                                    <Radio value="verifyEmployment">Verify Employment</Radio>
                                </Radio.Group>
                            </div>

                            {/* File Upload */}
                            <div className="mb-3">
                                {/* <medium style={{ color: "#555" }}>
                                    {   JOB_REQUIREMENTS[this.state.jobType].requiredMessage    }
                                </medium> */}
                                <medium>
                                    <span style={{ color: '#d93025', fontWeight: 600 }}>
                                        Mandatory :
                                    </span>{' '}
                                    {JOB_REQUIREMENTS[this.state.jobType].requiredMessage}
                                </medium>

                                <div style={{ marginTop: "10px" }}>
                                    <Upload beforeUpload={this.handleFileUpload} accept=".xlsx,.xls" showUploadList={false}>
                                        <Button icon={<UploadOutlined />}>Upload Excel</Button>
                                    </Upload>
                                </div>

                                {/* Show uploaded file name */}
                                {this.state.file && (
                                    <div style={{ marginTop: "8px", fontStyle: "italic", color: "#007cc3" }}>
                                        Uploaded File: <b>{this.state.file.name}</b>
                                    </div>
                                )}
                            </div>

                            {/* Error */}
                            {this.state.uploadError && <Alert type="error" message={this.state.uploadError} />}

                            {this.state.totalRecords > 0 && (
                                <div className="row mt-3">
                                    <div className="col-md-3">
                                        <Alert
                                            type="info"
                                            message={`Total Records : ${this.state.totalRecords}, Valid : ${this.state.validRecords}, Invalid : ${this.state.invalidRecords}`}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        {this.state.invalidRecords > 0 && (
                                            <p style={{ color: "red", marginTop: "8px", marginBottom: "0px" }}>
                                                Please fix invalid records before processing.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {this.state.totalRecords > 0 && (
                                <div className="row mt-4">

                                    {/* AUDIT FILE REPORT — only when invalid rows exist */}
                                    {this.state.invalidRecords > 0 && (
                                        <div className="col-md-2 col-6 mb-2">
                                            <Button 
                                                type="default" 
                                                block
                                                onClick={this.handleAuditReport}
                                                className={auditBtnCSS}
                                            >
                                                Audit File Report
                                            </Button>
                                        </div>
                                    )}

                                    {/* PROCESS WITH MODIGIE — always visible, disabled if invalid > 0 */}
                                    <div className="col-md-2 col-6 mb-2" >
                                        <Button
                                            block
                                            // disabled={
                                            //     this.state.invalidRecords > 0 
                                            // }
                                            // onClick={this.handleProcess}
                                            // className={
                                            //     this.state.invalidRecords > 0 
                                            //         ? processBtnDisabledCSS
                                            //         : processBtnCSS
                                            // }
                                            disabled={
                                                this.state.invalidRecords > 0 ||
                                                this.state.processingFileName === this.state.file?.name
                                            }
                                            onClick={this.handleProcess}
                                            className={
                                                this.state.invalidRecords > 0 || 
                                                this.state.processingFileName === this.state.file?.name
                                                ? processBtnDisabledCSS
                                                : processBtnCSS
                                            }
                                        >
                                            Process with Modigie
                                        </Button>
                                    </div>

                                    {/* DOWNLOAD OUTPUT EXCEL — only after batch completed */}
                                    {/* <div className="col-md-2 col-6 mb-2">
                                        {this.state.batchStatus === 'completed' ? (
                                            <Button
                                                block
                                                type="primary"
                                                onClick={() =>
                                                    window.open(`/modigie/download/${this.state.batchId}`, '_blank')
                                                }
                                            >
                                                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 6 }} />
                                                Download Output
                                            </Button>
                                        ) : (
                                            <Tooltip title="Available after processing completes">
                                                <Button block disabled>
                                                    Download Output
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div> */}
                                </div>
                            )}

                            {/* Progress */}
                            {/* {this.state.batchStatus && (
                                <Progress className="mt-3" percent={this.state.progress} />
                            )} */}

                            {/* <Progress
                                percent={this.state.progress}
                                status={
                                    this.state.batchStatus === 'completed'
                                    ? 'success'
                                    : this.state.batchStatus === 'processing'
                                    ? 'active'
                                    : 'exception'
                                }
                            /> */}

                            {this.state.lastFileName && (
                            <div className={lastBatchCardCSS}>
                                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#007cc3' }}>
                                Previous Modigie Batch
                                </div>

                                <div className="row ">
                                    <div className="col-md-3">
                                        <span className="label">File :</span>
                                        {/* <span className="value">{this.state.lastFileName}</span> */}
                                        <Tooltip title={this.state.lastFileName}>
                                            <span className={`${ellipsisText} value`}>
                                                {this.state.lastFileName}
                                            </span>
                                        </Tooltip>
                                    </div>

                                    <div className="col-md-4">
                                        <span className="label">Job Type :</span>
                                        <span className="value">
                                        {this.state.lastJobType === 'verifyEmployment'
                                            ? 'Verify Employment'
                                            : 'Enrich Mobile & Verify Employment'}
                                        </span>
                                    </div>

                                    <div className="col-md-2">
                                        <span className="label">Records :</span>
                                        <span className="value">{this.state.lastRecordCount}</span>
                                    </div>

                                    <div className="col-md-3">
                                        <span className="label">Status :</span>
                                        <span
                                        className="status"
                                        style={{
                                            color:
                                            this.state.batchStatus === 'completed'
                                                ? '#52c41a'
                                                : this.state.batchStatus === 'processing'
                                                ? '#faad14'
                                                : '#ff4d4f'
                                        }}
                                        >
                                        {this.state.batchStatus?.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            )}

                            {this.state.batchHistory.length > 0 && (
                            <div style={{ marginTop: 24 }} className={historyTableCSS}>
                            <h4 style={{ marginBottom: 12, color: '#28166f' }}>
                                Modigie Batch History
                            </h4>

                            <Table
                                rowKey="id"
                                columns={historyColumns}
                                dataSource={this.state.batchHistory}
                                pagination={{ pageSize: 10 }}
                                bordered
                                size="small"
                            />
                            </div>
                            )}

                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }
}

// Modigie.propTypes = {
//     logoutUser: PropTypes.func.isRequired,
//     auth: PropTypes.object.isRequired
// }

// const mapStateToProps = (state) => ({ auth: state.auth })
export default Modigie;
